"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import {
  IoArrowForward,
  IoCheckmarkCircle,
  IoClose,
  IoLockClosedOutline,
  IoShieldCheckmarkOutline,
  IoStar,
} from "react-icons/io5";
import { content } from "../content";
import {
  createConsultationOrder,
  normalizePhone,
  updateConsultationPaymentStatus,
  verifyConsultationPayment,
} from "../lib/consultationClient";
import { getPublicPricing } from "../lib/consultationApiClient";
import { trackLandingAbEvent } from "../lib/landingAbTest";
import { trackEvent } from "../lib/posthog";
import { getStoredAttributionPayload, getStoredUTMData } from "../utils/utmTracking";
import { useAuth } from "../context/AuthContext";
import InlineOtpVerification from "./InlineOtpVerification";

type AppStep = "booking" | "contact" | "review" | "confirmation";
type FlowMode = "organic" | "marketing";

type PricingMetadata = {
  badge?: string;
  badge_color?: string;
  short_benefit?: string;
  highlights?: string[];
  num_sessions?: number;
  num_followups?: number;
  astrologer_tier?: string;
  per_session_label?: string;
  best_for?: string[];
  recommended?: boolean;
};

type PricingRow = {
  id: string;
  duration_minutes: number;
  amount: number;
  currency: string;
  label: string | null;
  description: string | null;
  sort_order?: number | null;
  marketing_active?: boolean | null;
  metadata?: PricingMetadata;
};



type ConsultationAppFlowProps = {
  mode?: FlowMode;
  defaultConcernArea?: string;
  landingVariantId?: string;
  initialToken?: string;
  debugStep?: string;
  onExit?: () => void;
};

const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";

const cx = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(" ");

const formatMoney = (amount: number | string | null | undefined, currency = "INR") =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));

const loadRazorpay = () =>
  new Promise<boolean>((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });



const stepIndices: Record<AppStep, number> = {
  booking: 0,
  contact: 1,
  review: 2,
  confirmation: 3,
};

function StepProgress({ step, highestStepIndex, onStepClick }: { step: AppStep; highestStepIndex: number; onStepClick: (step: AppStep) => void }) {
  const steps: Array<{ id: AppStep; label: string; hindi: string }> = [
    { id: "booking", label: "Booking", hindi: "बुकिंग" },
    { id: "contact", label: "Details", hindi: "विवरण" },
    { id: "review", label: "Pay", hindi: "भुगतान" },
  ];
  const activeIndex = Math.max(0, steps.findIndex((item) => item.id === step));

  return (
    <div className="grid w-full grid-cols-3 gap-1 sm:gap-2" aria-label="Booking progress">
      {steps.map((item, index) => {
        const active = index <= activeIndex;
        const unlocked = index <= highestStepIndex;
        return (
          <button 
            key={item.id} 
            type="button"
            disabled={!unlocked}
            onClick={() => unlocked && onStepClick(item.id)}
            className="min-w-0 overflow-hidden text-left focus:outline-none disabled:cursor-not-allowed group"
          >
            <div className={cx("h-1 rounded-full sm:h-1.5 transition-colors", active ? "bg-[#ff7a1a]" : unlocked ? "bg-orange-200 group-hover:bg-orange-300" : "bg-slate-200")} />
            <div className={cx("mt-1 truncate text-center text-[10px] font-bold sm:mt-1.5 sm:text-[11px] transition-colors", active ? "text-slate-950" : unlocked ? "text-slate-700 group-hover:text-slate-900" : "text-slate-400")}>
              {item.label}
              <span className="hidden sm:inline"> / {item.hindi}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function Field({
  label,
  hindi,
  children,
  required = false,
}: {
  label: string;
  hindi?: string;
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <div className="flex w-full min-w-0 max-w-full flex-col">
      <label className="mb-2 block text-sm font-bold text-slate-800">
        {label}
        {hindi ? <span className="font-semibold text-slate-500"> / {hindi}</span> : null}
        {required ? <span className="text-orange-600"> *</span> : null}
      </label>
      {children}
    </div>
  );
}

const inputClass =
  "min-w-0 w-full max-w-full box-border rounded-xl border border-slate-200 bg-white px-4 py-3 text-[15px] font-semibold text-slate-950 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100";

export default function ConsultationAppFlow({
  mode = "organic",
  defaultConcernArea = "Other",
  landingVariantId = "",
  debugStep = "",
  onExit,
}: ConsultationAppFlowProps) {
  const marketingPricingOnly = mode === "marketing";
  const [step, setStep] = useState<AppStep>("booking");
  const [highestStepIndex, setHighestStepIndex] = useState(0);

  const goToStep = (newStep: AppStep) => {
    setStep(newStep);
    setHighestStepIndex((prev) => Math.max(prev, stepIndices[newStep]));
  };
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [pricingRows, setPricingRows] = useState<PricingRow[]>([]);
  const [selectedPricingId, setSelectedPricingId] = useState("");
  const [consultationId, setConsultationId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { user } = useAuth();
  const [showOtp, setShowOtp] = useState(false);
  const [successCard, setSuccessCard] = useState<{
    consultationId: number;
  } | null>(null);

  const selectedPricing = useMemo(
    () => pricingRows.find((row) => row.id === selectedPricingId) || pricingRows[0] || null,
    [pricingRows, selectedPricingId],
  );





  useEffect(() => {
    trackEvent('consultation_booking_viewed', { mode, landing_variant_id: landingVariantId || null });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadPricing = async () => {
      try {
        const data = await getPublicPricing({ marketingOnly: marketingPricingOnly });
        if (cancelled) return;

        const filtered = (data || []).filter((row) =>
          marketingPricingOnly ? row.marketing_active === true : row.marketing_active !== true,
        );
        const rows = (filtered.length ? filtered : data || []) as unknown as PricingRow[];
        setPricingRows(rows);
        const recommended = rows.find((r) => r.metadata?.recommended);
        setSelectedPricingId((current) => current || recommended?.id || rows[0]?.id || "");
      } catch (err: unknown) {
        if (cancelled) return;
        setError(`Unable to load consultation pricing: ${err instanceof Error ? err.message : 'Error'}`);
      }
    };

    void loadPricing();
    return () => {
      cancelled = true;
    };
  }, [marketingPricingOnly]);



  useEffect(() => {
    if (!debugStep) return;
    const normalizedStep = debugStep.toLowerCase().replace(/[_-]+/g, "");
    const stepMap: Record<string, AppStep> = {
      contact: "contact",
      number: "contact",
      details: "contact",
      userdetails: "contact",
      birthdetails: "contact",
      time: "contact",
      slot: "contact",
      choosetime: "contact",
      booking: "review",
      payment: "review",
      review: "review",
      confirmation: "confirmation",
      confirmed: "confirmation",
    };
    const nextStep = stepMap[normalizedStep];
    if (nextStep) goToStep(nextStep);
  }, [debugStep]);



  const handleContactContinue = async () => {
    const cleaned = normalizePhone(phone);
    if (cleaned.length !== 10) {
      setError("Enter a valid 10-digit WhatsApp number.");
      return;
    }
    if (!name.trim()) {
      setError("Full name is required.");
      return;
    }

    if (user?.phone !== cleaned) {
      setShowOtp(true);
      return;
    }

    setPhone(cleaned);
    trackEvent('consultation_contact_submitted', {
      pricing_id: selectedPricingId || null,
      duration_minutes: selectedPricing?.duration_minutes ?? null,
      amount_rs: selectedPricing?.amount ?? null,
    });
    trackEvent('consultation_payment_initiated', {
      pricing_id: selectedPricingId || null,
      duration_minutes: selectedPricing?.duration_minutes ?? null,
      amount_rs: selectedPricing?.amount ?? null,
    });
    goToStep("review");
  };

  const validateDetails = () => {
    const messages: string[] = [];
    if (!name.trim()) messages.push("Full name is required.");
    if (normalizePhone(phone).length !== 10) messages.push("Valid WhatsApp number is required.");
    if (!selectedPricing) messages.push("Consultation package is unavailable.");
    setError(messages[0] || "");
    return messages.length === 0;
  };

  const buildCheckoutSourceNote = () => [
    landingVariantId ? `landing_variant=${landingVariantId}` : null,
    `entry=${marketingPricingOnly ? "marketing" : "organic"}`,
    `page=${window.location.pathname}`,
  ].filter(Boolean).join(" | ");

  const startPayment = async () => {
    if (!selectedPricing || !validateDetails()) return;

    setBusy(true);
    setError("");
    try {
      const search = new URLSearchParams(window.location.search);
      const utmData = getStoredUTMData();
      const attribution = getStoredAttributionPayload();
      const order = await createConsultationOrder({
        consultation: {
          ...(consultationId ? { consultationId } : {}),
          name: name.trim(),
          contact: normalizePhone(phone),
          email: "",
          concern: defaultConcernArea,
          concernArea: defaultConcernArea,
          concernDetail: defaultConcernArea,
          concernSummary: defaultConcernArea,
          beneficiaryRelation: "self",
          durationMinutes: Number(selectedPricing.duration_minutes),
          pricingId: selectedPricing.id,
          pricing_id: selectedPricing.id,
          ...(marketingPricingOnly ? { marketingPricingOnly: true, marketingActive: true } : {}),
          source: "website",
          sourceLabel: "consultation_app",
          origin: window.location.href,
          attribution,
          timezone: browserTimezone,
          campaignCode: search.get("campaign_code") || utmData?.campaign_code || utmData?.campaign || "",
          utmSource: search.get("utm_source") || utmData?.utm_source || utmData?.source || "",
          utmMedium: search.get("utm_medium") || utmData?.utm_medium || "",
          utmCampaign: search.get("utm_campaign") || utmData?.utm_campaign || utmData?.campaign || "",
          utmContent: search.get("utm_content") || utmData?.utm_content || utmData?.ad || "",
          utmTerm: search.get("utm_term") || utmData?.utm_term || "",
          landingPage: utmData?.landing_page || window.location.pathname,
          landingVariantId: landingVariantId || undefined,
          assignmentId: search.get("ab_assignment") || undefined,
          sourceNote: buildCheckoutSourceNote(),
          fbclid: search.get("fbclid") || utmData?.fbclid || "",
          fbp: utmData?.fbp || "",
          fbc: utmData?.fbc || "",
          gclid: search.get("gclid") || utmData?.gclid || "",
          ttclid: search.get("ttclid") || utmData?.ttclid || "",
          campaignId: search.get("campaign_id") || utmData?.campaign_id || utmData?.utm_id || "",
          campaignName: search.get("campaign_name") || utmData?.campaign_name || utmData?.utm_campaign || "",
          adsetId: search.get("adset_id") || utmData?.adset_id || "",
          adsetName: search.get("adset_name") || utmData?.adset_name || utmData?.adset || "",
          adId: search.get("ad_id") || utmData?.ad_id || "",
          adName: search.get("ad_name") || utmData?.ad_name || utmData?.ad || "",
          creativeId: search.get("creative_id") || utmData?.creative_id || "",
          creativeName: search.get("creative_name") || utmData?.creative_name || "",
          placement: search.get("placement") || utmData?.placement || "",
          siteSourceName: search.get("site_source_name") || utmData?.site_source_name || "",
          platformPosition: search.get("platform_position") || utmData?.platform_position || "",
          videoId: search.get("video_id") || utmData?.video_id || "",
          videoName: search.get("video_name") || utmData?.video_name || "",
        },
      });

      void trackLandingAbEvent({
        event: "checkout_started",
        variantId: landingVariantId || undefined,
        assignmentId: search.get("ab_assignment") || undefined,
        metadata: {
          consultation_id: order.consultationId,
          pricing_id: selectedPricing.id,
          amount_rs: selectedPricing.amount,
          duration_minutes: selectedPricing.duration_minutes,
        },
      });

      setConsultationId(order.consultationId);
      const razorpayReady = await loadRazorpay();
      if (!razorpayReady || !window.Razorpay) {
        await updateConsultationPaymentStatus({
          consultationId: order.consultationId,
          status: "payment_abandoned",
        });
        throw new Error("Payment gateway failed to load. Please try again.");
      }

      const razorpay = new window.Razorpay({
        key: order.razorpay.key,
        amount: order.razorpay.amount,
        currency: order.razorpay.currency,
        name: "Astroji Consultation",
        description: selectedPricing.label || "Consultation booking",
        image: content.logoUrl,
        order_id: order.razorpay.orderId,
        prefill: {
          name: name.trim(),
          contact: normalizePhone(phone),
        },
        theme: { color: "#f97316" },
        handler: async (response: {
          razorpay_order_id: string;
          razorpay_payment_id: string;
          razorpay_signature: string;
        }) => {
          try {
            await verifyConsultationPayment({
              consultationId: order.consultationId,
              ...response,
            });

            if (typeof window.fbq === "function") {
              window.fbq("track", "Purchase", {
                value: Number(order.pricing.amount || selectedPricing.amount || 0),
                currency: order.pricing.currency || "INR",
                content_ids: [`consultation-${order.consultationId}`],
                content_type: "service",
                content_name: "Astroji Consultation",
                order_id: response.razorpay_order_id,
                payment_id: response.razorpay_payment_id,
              }, { eventID: response.razorpay_payment_id });
            }

            trackEvent("consultation_payment_completed", {
              booking_id: order.consultationId,
              booking_cost_rs: Number(order.pricing.amount || selectedPricing.amount || 0),
              duration_minutes: Number(order.pricing.durationMinutes || selectedPricing.duration_minutes),
            });

            void trackLandingAbEvent({
              event: "payment_success",
              variantId: landingVariantId || undefined,
              assignmentId: search.get("ab_assignment") || undefined,
              metadata: {
                consultation_id: order.consultationId,
                amount_rs: Number(order.pricing.amount || selectedPricing.amount || 0),
                order_id: response.razorpay_order_id,
                payment_id: response.razorpay_payment_id,
              },
            });

            setSuccessCard({
              consultationId: order.consultationId,
            });
            goToStep("confirmation");
          } catch (verificationError) {
            setError(
              verificationError instanceof Error
                ? verificationError.message
                : "Payment captured, but verification is pending.",
            );
          } finally {
            setBusy(false);
          }
        },
        modal: {
          ondismiss: () => {
            void updateConsultationPaymentStatus({
              consultationId: order.consultationId,
              status: "payment_abandoned",
            });
            setBusy(false);
          },
        },
      });

      razorpay.on("payment.failed", () => {
        void updateConsultationPaymentStatus({
          consultationId: order.consultationId,
          status: "payment_failed",
        });
        setBusy(false);
        setError("Payment failed. Please try again.");
      });

      razorpay.open();
    } catch (paymentError) {
      setBusy(false);
      setError(paymentError instanceof Error ? paymentError.message : "Unable to start payment.");
    }
  };

  const selectedAmount = selectedPricing
    ? formatMoney(selectedPricing.amount, selectedPricing.currency)
    : "--";



  /* ── Badge color mapping ── */
  const badgeColorMap: Record<string, string> = {
    emerald: "bg-emerald-100 text-emerald-700 border-emerald-300",
    orange: "bg-orange-100 text-orange-700 border-orange-300",
    purple: "bg-purple-100 text-purple-700 border-purple-300",
  };

  const recommendedRow = pricingRows.find((r) => r.metadata?.recommended);
  const otherRows = pricingRows.filter((r) => !r.metadata?.recommended);

  /* ── Single selectable card ── */
  const PackageCard = ({ row, featured = false }: { row: PricingRow; featured?: boolean }) => {
    const m = row.metadata || {};
    const isSelected = selectedPricingId === row.id;
    const isCeleb = m.astrologer_tier === "celebrity";

    return (
      <button
        type="button"
        onClick={() => {
          setSelectedPricingId(row.id);
          trackEvent('consultation_pricing_selected', {
            pricing_id: row.id,
            duration_minutes: row.duration_minutes,
            amount_rs: row.amount,
            currency: row.currency,
            label: row.label || null,
          });
        }}
        className={cx(
          "relative flex h-full w-full cursor-pointer flex-col overflow-hidden rounded-2xl border-2 text-left transition-all p-4 sm:p-5",
          isSelected
            ? isCeleb
              ? "border-purple-500 bg-purple-50/40 shadow-lg ring-2 ring-purple-500/20"
              : "border-orange-500 bg-orange-50/40 shadow-lg ring-2 ring-orange-500/20"
            : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-md",
        )}
      >
        {/* Selected checkmark */}
        {isSelected && (
          <div className={cx("absolute top-3 right-3 flex h-6 w-6 items-center justify-center rounded-full", isCeleb ? "bg-purple-600" : "bg-orange-600")}>
            <IoCheckmarkCircle className="text-white" size={16} />
          </div>
        )}

        {/* Badge */}
        {m.badge && (
          <div className={cx("mb-3 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-wider", badgeColorMap[m.badge_color || "orange"] || badgeColorMap.orange)}>
            {m.recommended && <IoStar size={12} />}
            {m.badge}
          </div>
        )}

        {/* Title + benefit */}
        <h3 className={cx("font-black text-slate-950", featured ? "text-xl sm:text-2xl" : "text-lg")}>
          {row.label || `${row.duration_minutes} minutes`}
        </h3>
        {m.short_benefit && (
          <p className="mt-1 text-sm text-slate-600">{m.short_benefit}</p>
        )}

        {/* Price row */}
        <div className="mt-3 flex items-baseline gap-2">
          <span className={cx("font-black text-slate-950", featured ? "text-3xl" : "text-2xl")}>
            {formatMoney(row.amount, row.currency)}
          </span>
          {m.per_session_label && (
            <span className="text-sm font-semibold text-slate-500">{m.per_session_label}</span>
          )}
        </div>

        {/* Metrics pills */}
        {(m.num_sessions || m.num_followups) ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {m.num_sessions ? (
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">
                {m.num_sessions} {m.num_sessions === 1 ? "Session" : "Sessions"}
              </span>
            ) : null}
            {m.num_followups ? (
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">
                {m.num_followups} Follow-{m.num_followups === 1 ? "Up" : "Ups"}
              </span>
            ) : null}
          </div>
        ) : null}

        {/* Highlights */}
        {m.highlights && m.highlights.length > 0 && (
          <ul className={cx("mt-4 grid gap-2", featured ? "" : "")}>
            {m.highlights.map((h, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <IoCheckmarkCircle className="mt-0.5 shrink-0 text-emerald-500" size={16} />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-auto w-full pt-4 flex flex-col justify-end">
          {/* Best for tags */}
          {m.best_for && m.best_for.length > 0 && (
            <div className="mb-4 min-h-[76px]">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Best For</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {m.best_for.map((tag) => (
                  <span key={tag} className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">{tag}</span>
                ))}
              </div>
            </div>
          )}

          {/* Selection CTA text */}
          <div className={cx(
            "flex min-h-[40px] w-full items-center justify-center rounded-xl text-sm font-black transition-colors",
            isSelected
              ? isCeleb
                ? "bg-purple-600 text-white"
                : "bg-orange-600 text-white"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200",
          )}>
            {isSelected ? "✓ Selected" : "Choose Package"}
          </div>
        </div>
      </button>
    );
  };

  const renderBooking = () => (
    <div className="mx-auto min-w-0 max-w-6xl pb-24 pt-4 sm:pb-28 sm:pt-8">
      {/* ── Section 1: Header ── */}
      <div className="mb-6 text-center sm:mb-8">
        <h1 className="text-2xl font-black tracking-tight text-slate-950 sm:text-3xl">Find the right consultation</h1>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-600">
          Book a private consultation with our expert astrologers. Choose the option that best matches the level of guidance you need.
        </p>
      </div>

      {/* ── All packages: stacked on mobile, 3-col on desktop ── */}
      <div className="mb-8 grid gap-4 lg:grid-cols-3 lg:items-stretch">
        {/* On mobile: recommended first (full width), others below */}
        {/* On desktop: all 3 side-by-side */}
        {recommendedRow && <PackageCard row={recommendedRow} featured />}
        {otherRows.map((row) => (
          <PackageCard key={row.id} row={row} />
        ))}
      </div>

      {/* ── Section 4: Comparison table ── */}
      {pricingRows.length > 1 && (
        <div className="mb-8">
          <p className="mb-3 text-xs font-black uppercase tracking-[0.18em] text-slate-400">Compare Packages</p>
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full min-w-[420px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="p-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500">Feature</th>
                  {pricingRows.map((r) => (
                    <th key={r.id} className={cx("p-3 text-center text-xs font-bold uppercase tracking-wider", selectedPricingId === r.id ? "text-orange-700" : "text-slate-500")}>
                      {(r.label || "").split(" ")[0]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr>
                  <td className="p-3 text-center font-semibold text-slate-700">Sessions</td>
                  {pricingRows.map((r) => <td key={r.id} className="p-3 text-center font-bold text-slate-900">{r.metadata?.num_sessions ?? 1}</td>)}
                </tr>
                <tr>
                  <td className="p-3 text-center font-semibold text-slate-700">Follow-Ups</td>
                  {pricingRows.map((r) => <td key={r.id} className="p-3 text-center font-bold text-slate-900">{r.metadata?.num_followups ?? 0}</td>)}
                </tr>
                <tr>
                  <td className="p-3 text-center font-semibold text-slate-700">Duration</td>
                  {pricingRows.map((r) => {
                    const sessions = r.metadata?.num_sessions ?? 1;
                    return <td key={r.id} className="p-3 text-center font-bold text-slate-900">{r.duration_minutes} min{sessions > 1 ? "/session" : ""}</td>;
                  })}
                </tr>
                <tr>
                  <td className="p-3 text-center font-semibold text-slate-700">Astrologer</td>
                  {pricingRows.map((r) => <td key={r.id} className="p-3 text-center font-bold text-slate-900">{r.metadata?.astrologer_tier === "celebrity" ? "Vaibhav ⭐" : "Expert"}</td>)}
                </tr>
                <tr className="bg-slate-50">
                  <td className="p-3 text-center font-bold text-slate-900">Price</td>
                  {pricingRows.map((r) => <td key={r.id} className="p-3 text-center font-black text-slate-900">{formatMoney(r.amount, r.currency)}</td>)}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Section 5: Help choosing ── */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <p className="text-sm font-black text-slate-950">Need help choosing?</p>
        <ul className="mt-3 grid gap-2 text-sm text-slate-600">
          {pricingRows.map((r) => {
            const m = r.metadata || {};
            return (
              <li key={r.id} className="flex items-start gap-2">
                <span className="mt-0.5 text-orange-500">•</span>
                <span>
                  <span className="font-bold text-slate-800">{r.label}</span>
                  {m.short_benefit ? ` — ${m.short_benefit.charAt(0).toLowerCase()}${m.short_benefit.slice(1)}` : ""}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );

  const renderContact = () => (
    <div className="mx-auto min-w-0 max-w-2xl py-6 sm:py-10">
      <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_12px_36px_rgba(15,23,42,0.06)] sm:p-6">
        <p className="text-xs font-black uppercase tracking-[0.18em] text-orange-600">Your Details</p>
        <h1 className="mt-1.5 text-xl font-black text-slate-950 sm:text-2xl">How should we reach you?</h1>
        <p className="mt-1.5 text-sm text-slate-600">We only need a couple of details to start.</p>

        <div className="mt-6 grid w-full min-w-0 gap-5">
          <Field label="Full name" hindi="पूरा नाम" required>
            <input value={name} onChange={(event) => setName(event.target.value)} className={inputClass} autoComplete="name" />
          </Field>
          
          <Field label="WhatsApp number" hindi="व्हाट्सएप नंबर" required>
            <div className="flex w-full max-w-full box-border overflow-hidden rounded-xl border border-slate-200 bg-white focus-within:border-orange-400 focus-within:ring-4 focus-within:ring-orange-100">
              <div className="flex min-h-[50px] items-center gap-2 border-r border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-800">
                +91
              </div>
              <input
                value={phone}
                onChange={(event) => {
                  setPhone(normalizePhone(event.target.value));
                  if (error) setError("");
                }}
                className="min-h-[50px] min-w-0 flex-1 border-0 px-4 text-base font-bold text-slate-950 outline-none"
                placeholder="10-digit number"
                inputMode="numeric"
                autoComplete="tel-national"
                maxLength={10}
              />
            </div>
          </Field>
          
          {showOtp && (
            <div className="mt-2 rounded-xl border border-orange-200 bg-orange-50 p-4">
              <InlineOtpVerification
                phone={normalizePhone(phone)}
                onVerified={() => {
                  setShowOtp(false);
                  setPhone(normalizePhone(phone));
                  setTimeout(() => goToStep("review"), 100);
                }}
                onCancel={() => setShowOtp(false)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderReview = () => {
    return (
      <div className="mx-auto min-w-0 py-4 sm:max-w-5xl sm:py-6">
        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-orange-600">Review and pay</p>
            <h1 className="mt-1.5 text-2xl font-black text-slate-950 sm:text-3xl">Confirm your booking</h1>
            <p className="mt-1.5 text-sm text-slate-600">Check your details before secure payment.</p>
          </div>
        </div>

        <div className="mb-3 rounded-2xl border border-orange-200 bg-orange-50/80 p-3 shadow-sm sm:p-4">
          <p className="text-sm font-semibold text-slate-800">
            Our team will reach out after payment to schedule your session at a comfortable time. We will also collect your birth details at that time.
          </p>
        </div>

        <div className="grid min-w-0 gap-3 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-4">
          <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:p-5">
            <h2 className="text-base font-black text-slate-950 sm:text-lg">Your details / आपका विवरण</h2>
            <div className="mt-3 divide-y divide-slate-200 rounded-xl border border-slate-200 px-3 sm:px-4">
              {[
                ["Name", name],
                ["WhatsApp", `+91 ${normalizePhone(phone)}`],
              ].map(([label, value]) => (
                <div key={label} className="grid gap-1.5 py-2.5 sm:grid-cols-[150px_1fr] sm:py-3">
                  <div className="text-xs font-semibold text-slate-500 sm:text-sm">{label}</div>
                  <div className="min-w-0 whitespace-pre-wrap break-words text-sm font-bold text-slate-950 sm:text-right">{value}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:p-5">
            <div className="flex items-center gap-2 text-sm font-black text-slate-700">
              <IoLockClosedOutline className="text-orange-600" />
              Pricing summary
            </div>
            <div className="mt-3 text-4xl font-black text-slate-950">{selectedAmount}</div>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Your consultation time will be scheduled by our team after successful payment.
            </p>

            <div className="mt-4 rounded-xl bg-slate-50 p-4 text-sm text-slate-700">
              <div className="flex justify-between gap-4">
                <span>Plan</span>
                <span className="min-w-0 break-words text-right font-bold text-slate-950">{selectedPricing?.label || "Consultation"}</span>
              </div>
              <div className="mt-3 flex justify-between gap-4">
                <span>Duration</span>
                <span className="font-bold text-slate-950">{selectedPricing?.duration_minutes || 20} mins</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderConfirmation = () => (
    <div className="mx-auto min-w-0 max-w-3xl py-8 text-center">
      <div className="rounded-2xl border border-emerald-200 bg-white p-5 shadow-[0_12px_36px_rgba(15,23,42,0.06)] sm:p-8">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
          <IoCheckmarkCircle size={36} />
        </div>
        <h1 className="mt-5 text-2xl font-black text-slate-950 sm:text-4xl">Booking confirmed</h1>
        <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">
          Payment is complete. You will be contacted by our team on WhatsApp to fix the time slots and collect your birth details.
        </p>
        <div className="mx-auto mt-5 max-w-xl rounded-xl bg-slate-50 p-4 text-left">
          <div className="text-sm font-black uppercase tracking-[0.18em] text-slate-400">
            Consultation #{successCard?.consultationId || consultationId}
          </div>
          <div className="mt-2 text-lg font-black text-slate-950">
            Slot to be scheduled over WhatsApp
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep = () => {
    if (step === "contact") return renderContact();
    if (step === "booking") return renderBooking();
    if (step === "review") return renderReview();
    return renderConfirmation();
  };


  return (
    <div className="box-border min-h-dvh w-full max-w-full overflow-x-hidden bg-[#faf7f2] text-slate-950 [&_*]:box-border">
      <header className="sticky top-0 z-30 w-full max-w-full overflow-hidden border-b border-slate-200/80 bg-white/95 shadow-[0_4px_18px_rgba(15,23,42,0.04)] backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-4 py-2 sm:px-6 sm:py-2.5">
          <Link prefetch={false} href="/" className="flex items-center">
            <img src={content.logoUrl} alt="Astroji" className="h-auto w-24 object-contain sm:w-28" />
          </Link>
          <div className="hidden min-w-[320px] sm:block">
            <StepProgress step={step} highestStepIndex={highestStepIndex} onStepClick={goToStep} />
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden items-center gap-2 rounded-full bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 sm:flex">
              <IoShieldCheckmarkOutline />
              Secure
            </div>
            {onExit ? (
              <button
                type="button"
                onClick={onExit}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 transition hover:border-orange-200 hover:text-orange-700"
                aria-label="Close booking"
              >
                <IoClose />
              </button>
            ) : null}
          </div>
        </div>
        <div className="border-t border-slate-100 px-4 py-2 sm:hidden">
          <StepProgress step={step} highestStepIndex={highestStepIndex} onStepClick={goToStep} />
        </div>
      </header>

      <main className="mx-auto box-border min-h-[calc(100dvh-76px)] w-full max-w-full overflow-x-hidden px-4 pb-24 sm:max-w-6xl sm:px-6">
        {error ? (
          <div className="mx-auto mt-4 max-w-3xl rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
            {error}
          </div>
        ) : null}
        {renderStep()}
      </main>

      {step !== "confirmation" ? (
        <footer className="fixed bottom-0 left-0 right-0 z-50 w-full max-w-full overflow-x-hidden border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.06)] backdrop-blur">
          <div className="mx-auto flex w-full items-center justify-center gap-3 sm:max-w-xl">
            {step === "booking" && (
              <button
                type="button"
                onClick={() => goToStep("contact")}
                disabled={!selectedPricingId}
                className="flex min-h-[42px] w-full flex-1 items-center justify-center gap-2 rounded-xl bg-orange-600 px-5 text-sm font-black text-white shadow-sm shadow-orange-500/20 transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Continue to Details
                <IoArrowForward />
              </button>
            )}
            {step === "contact" && !showOtp && (
              <button
                type="button"
                onClick={() => void handleContactContinue()}
                disabled={busy || normalizePhone(phone).length !== 10 || !name.trim()}
                className="flex min-h-[42px] w-full flex-1 items-center justify-center gap-2 rounded-xl bg-orange-600 px-5 text-sm font-black text-white shadow-sm shadow-orange-500/20 transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? "Checking..." : "Review and Pay"}
                <IoArrowForward />
              </button>
            )}
            {step === "review" && (
              <button
                type="button"
                onClick={() => void startPayment()}
                disabled={busy}
                className="flex min-h-[42px] w-full flex-1 items-center justify-center gap-2 rounded-xl bg-orange-600 px-5 text-sm font-black text-white shadow-sm shadow-orange-500/20 transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? "Opening payment..." : `Pay ${selectedAmount}`}
                <IoArrowForward />
              </button>
            )}
          </div>
        </footer>
      ) : null}
    </div>
  );
}
